import React, { useEffect, useMemo, useState } from "react";
import { EditorDialog, Button } from "./ScheduleEditorUI";
import { Box, Checkbox, Typography } from "@mui/material";

const CHIP_LIMIT = 4;

/**
 * Scalable category picker: summary + "Choose categories" searchable modal.
 * Works with hundreds of options without dumping checkboxes into the page.
 */
const WindowCategoryPicker = ({
  options = [],
  value = [],
  onChange,
  label = "Appointment types",
  required = true,
  disabled = false,
  buttonLabel = "Change types",
  emptyHint = "No appointment types available. Add appointment types in schedule settings first.",
}) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!open) return;
    setDraft(value);
    setQuery("");
  }, [open, value]);

  const optionById = useMemo(() => {
    const map = new Map();
    options.forEach((option) => map.set(option.value, option));
    return map;
  }, [options]);

  const selectedOptions = useMemo(
    () => value.map((id) => optionById.get(id)).filter(Boolean),
    [value, optionById]
  );

  const filteredOptions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((option) => [option.name, option.organizationName, option.organizationRef].filter(Boolean).join(" ").toLowerCase().includes(q));
  }, [options, query]);

  const groups = useMemo(() => {
    const grouped = new Map();
    filteredOptions.forEach(option => {
      const key = option.organizationRef || "";
      if (!grouped.has(key)) grouped.set(key, { name: option.organizationName || key, options: [] });
      grouped.get(key).options.push(option);
    });
    return [...grouped.entries()];
  }, [filteredOptions]);

  const allFilteredSelected =
    filteredOptions.length > 0 && filteredOptions.every((option) => draft.includes(option.value));

  const toggleDraft = (id) => {
    setDraft((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
  };

  const selectAllFiltered = () => {
    setDraft((prev) => {
      const next = new Set(prev);
      filteredOptions.forEach((option) => next.add(option.value));
      return [...next];
    });
  };

  const clearFiltered = () => {
    const filteredIds = new Set(filteredOptions.map((option) => option.value));
    setDraft((prev) => prev.filter((id) => !filteredIds.has(id)));
  };

  const apply = () => {
    onChange(draft);
    setOpen(false);
  };

  const visibleChips = selectedOptions.slice(0, CHIP_LIMIT);
  const hiddenCount = Math.max(0, selectedOptions.length - CHIP_LIMIT);

  return (
    <div className="scheduling-window-categories">
      <label>
        {label}
        {required ? <span style={{ color: "red" }}> *</span> : null}
      </label>

      {!options.length ? (
        <p className="scheduling-empty mb-0">{emptyHint}</p>
      ) : (
        <>
          <div className="scheduling-category-summary">
            <div className="scheduling-category-chips">
              {selectedOptions.length === 0 ? (
                <span className="scheduling-category-none">None selected</span>
              ) : (
                <>
                  {visibleChips.map((option) => (
                    <span key={option.value} className="scheduling-category-chip" title={[option.name, option.organizationName || option.organizationRef].filter(Boolean).join(" · ")}>
                      {option.name}
                      {option.organizationRef ? ` · ${option.organizationName || option.organizationRef}` : ""}
                    </span>
                  ))}
                  {hiddenCount > 0 ? (
                    <span className="scheduling-category-chip more">+{hiddenCount} more</span>
                  ) : null}
                </>
              )}
            </div>
            <Button
              type="button"
              variant="secondary"
              disabled={disabled}
              onClick={() => setOpen(true)}
            >
              {buttonLabel}
            </Button>
          </div>
          <div className="scheduling-category-count">
            {selectedOptions.length} of {options.length} selected
          </div>
        </>
      )}

      <EditorDialog open={open} onClose={() => setOpen(false)} title="Choose appointment types"
        actions={<><Button variant="secondary" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={apply}>Apply</Button></>}>
          <div className="scheduling-category-modal-toolbar">
            <input
              type="search"
              className="form-control"
              placeholder="Search appointment types…"
              aria-label="Search appointment types"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              autoFocus
            />
            <div className="scheduling-category-modal-actions">
              <Button type="button" variant="secondary" onClick={selectAllFiltered} disabled={!filteredOptions.length}>
                {allFilteredSelected ? "All filtered selected" : "Select filtered"}
              </Button>
              <Button type="button" variant="secondary" onClick={clearFiltered} disabled={!draft.length}>
                Clear filtered
              </Button>
            </div>
          </div>
          <div className="scheduling-category-modal-meta">
            Showing {filteredOptions.length} of {options.length} · Draft selection: {draft.length}
          </div>
          <div className="scheduling-category-modal-list" role="group" aria-label="Available appointment types">
            {filteredOptions.length === 0 ? (
              <p className="scheduling-empty mb-0">No categories match “{query.trim()}”.</p>
            ) : (
              groups.map(([org, group]) => <Box key={org} component="fieldset" sx={{ border: 0, p: 0, m: 0, minWidth: 0 }}>
                {group.name && <Typography component="legend" sx={{ px: 2, py: 1, fontSize: 13, fontWeight: 600, color: "text.secondary", backgroundColor: "grey.50", width: "100%" }}>{group.name}</Typography>}
                {group.options.map((option) => {
                const checked = draft.includes(option.value);
                return (
                  <label
                    key={option.value}
                    className={`scheduling-category-modal-row${checked ? " selected" : ""}`}
                  >
                    <Checkbox
                      size="small"
                      checked={checked}
                      onChange={() => toggleDraft(option.value)}
                    />
                    <span>{option.name}</span>
                  </label>
                );
              })}</Box>)
            )}
          </div>
      </EditorDialog>
    </div>
  );
};

export default WindowCategoryPicker;
